import React, { useMemo, useState } from "react";
import { gql, useQuery } from "urql";
import { Link } from "wouter";
import { sortBy } from "lodash";

import { write, utils } from "xlsx";
import { FileSpreadsheet } from "lucide-react";

import { Column, Grid } from "../../../components/src";
import { saveAs } from "file-saver";

import { BaseCalendar, BaseCalendarFilterInput, CalendarStateEnum, Party, PartyStatusEnum, PartyType } from "../types.generated";

import "./CalendarsPage.css";

type MasterCalendarRow = Record<string, string>;

type MasterCalendarFund = {
    fundName: string;
    // The ISINs of the enabled classes of the fund, separated by commas
    isin: string;
    calendarEvents?: Array<{ date: string; state: CalendarStateEnum }> | null;
};

function isoToLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
}

function getMasterCalendarColumnFields(rows: MasterCalendarRow[]): string[] {
    if (!rows.length) {
        return [];
    }

    return Object.keys(rows[0])
        .filter((key) => key.startsWith("Date "))
        .sort((left, right) => Number(left.replace("Date ", "")) - Number(right.replace("Date ", "")));
}

function buildMasterCalendarSheetRows(funds: MasterCalendarFund[], state: CalendarStateEnum): MasterCalendarRow[] {
    const sortedFunds = sortBy(funds, (fund) => fund.fundName);
    const datesByFund = sortedFunds.map((fund) => {
        const events = fund.calendarEvents || [];
        return events
            .filter((event) => event.state === state)
            .map((event) => event.date)
            .sort();
    });

    const maxDates = datesByFund.reduce((max, dates) => Math.max(max, dates.length), 0);
    const rows: MasterCalendarRow[] = [];

    for (let index = 0; index < sortedFunds.length; index++) {
        const row: MasterCalendarRow = {
            Fund: sortedFunds[index].fundName,
            ISIN: sortedFunds[index].isin
        };

        for (let dateIndex = 0; dateIndex < maxDates; dateIndex++) {
            const columnName = "Date " + (dateIndex + 1);
            const value = datesByFund[index][dateIndex];
            row[columnName] = typeof value === "undefined" ? "" : value;
        }

        rows.push(row);
    }

    return rows;
}

// Grid does not flatten nested children arrays, so all columns must be passed as one flat array
function masterCalendarColumns(dateFields: string[]): React.ReactElement[] {
    const columns = [
        <Column key="Fund" field="Fund" title="Fund" className="nowrap" />,
        <Column key="ISIN" field="ISIN" title="ISIN" className="nowrap" />
    ];

    for (const dateField of dateFields) {
        columns.push(<Column key={dateField} field={dateField} title={dateField} className="nowrap" />);
    }

    return columns;
}

function masterCalendarRowsToAoa(rows: MasterCalendarRow[]): Array<Array<string | number | Date>> {
    const dateFields = getMasterCalendarColumnFields(rows);
    const aoa: Array<Array<string | number | Date>> = [["Fund", "ISIN", ...dateFields]];

    for (const row of rows) {
        const line: Array<string | number | Date> = [row.Fund, row.ISIN];
        for (const field of dateFields) {
            const value = row[field];
            if (!value) {
                line.push("");
            } else {
                line.push(isoToLocalDate(value));
            }
        }
        aoa.push(line);
    }

    return aoa;
}

function applyExcelDateFormats(sheet: Record<string, any>): void {
    if (!sheet["!ref"]) {
        return;
    }

    const range = utils.decode_range(sheet["!ref"]);
    for (let row = 1; row <= range.e.r; row++) {
        for (let col = 1; col <= range.e.c; col++) {
            const cell = sheet[utils.encode_cell({ r: row, c: col })];
            if (cell && (cell.t === "d" || cell.t === "n")) {
                cell.z = "YYYY-MM-DD";
            }
        }
    }
}

function exportMasterCalendarFundsXlsx(closedDaysRows: MasterCalendarRow[], halfDaysRows: MasterCalendarRow[], year: string): void {
    if (!closedDaysRows.length && !halfDaysRows.length) {
        return;
    }

    const wb = utils.book_new();
    const sheets = [
        { name: "ClosedDays", rows: closedDaysRows },
        { name: "HalfDays", rows: halfDaysRows }
    ];

    for (const sheetDef of sheets) {
        const aoa = sheetDef.rows.length ? masterCalendarRowsToAoa(sheetDef.rows) : [["Fund", "ISIN"]];
        const sheet = utils.aoa_to_sheet(aoa, { cellDates: true, dateNF: "YYYY-MM-DD" });
        applyExcelDateFormats(sheet);
        utils.book_append_sheet(wb, sheet, sheetDef.name);
    }

    const wbout = write(wb, {
        bookType: "xlsx",
        bookSST: false,
        type: "array",
        cellDates: true
    });

    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "master_calendar_funds_" + year + ".xlsx");
}

const GetCalendars = gql`
    query baseCalendars($filter: BaseCalendarFilterInput) {
        baseCalendars(filter: $filter) {
            _id
            name
            description
            clientIds
            year
            baseEvents {
                event
                date
                state
            }
            status
            source
        }
    }
`;

const GetFunds = gql`
    query parties($filter: PartyFilterInput) {
        parties(filter: $filter) {
            _id
            name
            fundInfo {
                calendarNames
            }
        }
    }
`;

const GetMasterCalendarFunds = gql`
    query masterCalendarFunds($filter: PartyFilterInput) {
        parties(filter: $filter) {
            _id
            name
            fundInfo {
                classes {
                    enabled
                    instrument {
                        isin
                    }
                }
                calendar {
                    calendarEvents {
                        date
                        state
                    }
                }
            }
        }
    }
`;

export const CalendarsPage = (): React.ReactElement => {
    const [year, setYear] = useState(String(new Date().getFullYear()));
    const filter: BaseCalendarFilterInput = {};

    const [{ fetching: loading, error, data }] = useQuery({
        query: GetCalendars,
        variables: { filter },
        requestPolicy: "cache-and-network"
    });
    const [{ fetching: loadingFunds, error: errorFunds, data: dataFunds }] = useQuery({
        query: GetFunds,
        variables: {
            filter: { typeIn: [PartyType.Fund], statusIn: [PartyStatusEnum.Confirmed] }
        },
        requestPolicy: "cache-and-network"
    });
    const [{ fetching: loadingMasterFunds, error: errorMasterFunds, data: dataMasterFunds }] = useQuery({
        query: GetMasterCalendarFunds,
        variables: {
            filter: { typeIn: [PartyType.Fund], statusIn: [PartyStatusEnum.Confirmed] }
        },
        requestPolicy: "cache-and-network"
    });

    const funds: Party[] = dataFunds && dataFunds.parties ? dataFunds.parties : [];

    const masterCalendarFunds = useMemo(() => {
        const masterFunds: Party[] = dataMasterFunds && dataMasterFunds.parties ? dataMasterFunds.parties : [];
        return masterFunds.map((fund) => ({
            fundName: fund.name,
            // A class that is not explicitly disabled counts as enabled
            isin: (fund.fundInfo?.classes || [])
                .filter((fundClass) => fundClass.enabled !== false)
                .map((fundClass) => fundClass.instrument?.isin)
                .filter((isin) => !!isin)
                .sort()
                .join(", "),
            calendarEvents: (fund.fundInfo?.calendar?.calendarEvents || []).filter((event) => event.date.startsWith(year + "-"))
        }));
    }, [dataMasterFunds, year]);
    const closedDaysRows = useMemo(
        () => buildMasterCalendarSheetRows(masterCalendarFunds, CalendarStateEnum.Closed),
        [masterCalendarFunds]
    );
    const halfDaysRows = useMemo(
        () => buildMasterCalendarSheetRows(masterCalendarFunds, CalendarStateEnum.EarlyClose),
        [masterCalendarFunds]
    );
    const closedDayColumns = useMemo(() => getMasterCalendarColumnFields(closedDaysRows), [closedDaysRows]);
    const halfDayColumns = useMemo(() => getMasterCalendarColumnFields(halfDaysRows), [halfDaysRows]);

    if (loading || loadingFunds) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (errorFunds) return <p>error: {JSON.stringify(errorFunds, null, 2)}</p>;

    const baseCalendars: BaseCalendar[] = data && data.baseCalendars ? data.baseCalendars : [];
    const masterCalendarError = errorMasterFunds ? JSON.stringify(errorMasterFunds, null, 2) : null;

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-4/12 px-2 mb-4">
                    <Grid header="Fund calendars" data={funds} sortable tableClassName="table-xs">
                        <Column
                            field="name"
                            title="Fund"
                            format={(value, item) => {
                                return item && item._id ? <Link to={"/parties/" + item._id}>{value}</Link> : value;
                            }}
                        />
                        <Column
                            field="fundInfo"
                            title="Calendars"
                            format={(value, item) => {
                                const calendarNames = item?.fundInfo?.calendarNames || [];
                                return calendarNames.length ? (
                                    <Link to={"/calendars/" + item._id}>{calendarNames.join(", ")}</Link>
                                ) : (
                                    ""
                                );
                            }}
                        />
                    </Grid>
                </div>
                <div className="w-full md:w-8/12 px-2 mb-4">
                    <Grid header="All calendars" data={baseCalendars} sortable tableClassName="table-xs">
                        <Column
                            field="name"
                            title="Name"
                            format={(value, item) => {
                                return item && item._id ? <Link to={"/calendars/" + item._id}>{value}</Link> : value;
                            }}
                        />
                        <Column
                            field="description"
                            title="Description"
                            format={(value, item) => {
                                return item && item._id ? <Link to={"/calendars/" + item._id}>{value}</Link> : value;
                            }}
                        />
                        <Column
                            field="year"
                            title={"Year"}
                            format={(value, item) => {
                                return item && item._id ? <Link to={"/calendars/" + item._id}>{value}</Link> : value;
                            }}
                        />
                    </Grid>
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-2">
                    <h4>Master calendar</h4>
                </div>
                <div className="w-full px-2 mb-4" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <label className="mb-0" htmlFor="master-calendar-year">
                        Year
                    </label>
                    <input
                        id="master-calendar-year"
                        className="form-control"
                        type="number"
                        min={2000}
                        max={2100}
                        value={year}
                        onChange={(event) => setYear(event.target.value)}
                        style={{ width: "7.5em", display: "inline-block" }}
                    />
                    <button
                        id="export-master-calendar"
                        className="export-link"
                        type="button"
                        disabled={loadingMasterFunds || !masterCalendarFunds.length}
                        title="Download master calendar"
                        onClick={() => exportMasterCalendarFundsXlsx(closedDaysRows, halfDaysRows, year)}
                        style={{ marginLeft: "auto", display: "flex", alignItems: "center", cursor: "pointer" }}
                    >
                        <FileSpreadsheet className="Excel" style={{ float: "none", margin: 0, width: "20px", height: "20px" }} />
                    </button>
                </div>
                {loadingMasterFunds ? <div className="w-full px-2 mb-4">Loading master calendar...</div> : null}
                {masterCalendarError ? <div className="w-full px-2 mb-4">Master calendar error: {masterCalendarError}</div> : null}
                {!loadingMasterFunds && !masterCalendarError ? (
                    <React.Fragment>
                        <div className="w-full px-2 mb-4">
                            <h4>Closed days</h4>
                            <div className="master-calendar-scroll">
                                <Grid data={closedDaysRows} sortable tableClassName="table-xs master-calendar-table" showHeader={false} hideDownload>
                                    {masterCalendarColumns(closedDayColumns)}
                                </Grid>
                            </div>
                        </div>
                        <div className="w-full px-2 mb-4">
                            <h4>Half days</h4>
                            <div className="master-calendar-scroll">
                                <Grid data={halfDaysRows} sortable tableClassName="table-xs master-calendar-table" showHeader={false} hideDownload>
                                    {masterCalendarColumns(halfDayColumns)}
                                </Grid>
                            </div>
                        </div>
                    </React.Fragment>
                ) : null}
            </div>
        </div>
    );
};
